import { Settings } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { resetToday } from '../../data/commands/tiles';
import { useCommandEnv, useData } from '../../data/hooks';
import { listTiles } from '../../data/repos/tiles';
import { useQuery } from '../../data/useQuery';
import { BentoGrid } from '../../tiles/BentoGrid';
import { EditableGrid } from '../../tiles/EditableGrid';
import { TODAY, visibleTiles, type TileRow } from '../../tiles/layout';
import { TileActionsSheet } from '../../tiles/TileActionsSheet';
import { TilePicker } from '../../tiles/TilePicker';
import { TileSettingsSheet } from '../../tiles/TileSettingsSheet';
import { Button } from '../../ui/Button';
import { Sheet } from '../../ui/Sheet';
import { SyncStatusLine } from '../shell/SyncStatusLine';
import { Onboarding, useNeedsOnboarding } from './Onboarding';

const NO_ROWS: TileRow[] = [];

export function TodayScreen() {
  const env = useCommandEnv();
  const { tz } = useData();
  const [editing, setEditing] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [actionsFor, setActionsFor] = useState<string | null>(null);
  const [settingsFor, setSettingsFor] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const needsOnboarding = useNeedsOnboarding();

  const { data: rows = NO_ROWS } = useQuery(['tiles'], (db) => listTiles(db, TODAY));
  const list = useMemo(
    () => visibleTiles(rows, env.usable, { includeHidden: editing }),
    [rows, env.usable, editing],
  );
  const dateLabel = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    timeZone: tz,
  }).format(new Date());

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-end justify-between gap-2">
        <div>
          <p className="text-sm text-muted">{dateLabel}</p>
          <h1 className="text-[26px] font-bold leading-tight">Today</h1>
          <SyncStatusLine />
        </div>
        <div className="flex items-center gap-1">
          {editing ? (
            <Button variant="primary" onClick={() => setEditing(false)}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="ghost" aria-label="Edit Today" onClick={() => setEditing(true)}>
                Edit
              </Button>
              <Link
                to="/settings"
                aria-label="Settings"
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full text-muted"
              >
                <Settings aria-hidden className="h-5 w-5" />
              </Link>
            </>
          )}
        </div>
      </header>

      {editing ? (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setPickerOpen(true)}>Add tile</Button>
          <Button variant="ghost" onClick={() => setConfirmReset(true)}>
            Reset to default
          </Button>
        </div>
      ) : null}

      {needsOnboarding && !editing ? <Onboarding /> : null}
      {!editing && list.length === 0 ? (
        <p className="text-muted">Nothing on Today yet. Tap Edit to add a tile.</p>
      ) : null}
      {editing ? (
        <EditableGrid tiles={list} onMore={setActionsFor} />
      ) : (
        <BentoGrid tiles={list} linkTitles />
      )}

      <TilePicker open={pickerOpen} onOpenChange={setPickerOpen} />
      <TileActionsSheet
        tileId={actionsFor}
        list={list}
        onClose={() => setActionsFor(null)}
        onOpenSettings={(id) => {
          setActionsFor(null);
          setSettingsFor(id);
        }}
      />
      <TileSettingsSheet tileId={settingsFor} rows={rows} onClose={() => setSettingsFor(null)} />
      <Sheet
        open={confirmReset}
        onOpenChange={setConfirmReset}
        title="Reset Today?"
        description="Today goes back to the default tiles. Your data isn't touched."
      >
        <div className="flex gap-2">
          <Button
            variant="primary"
            onClick={async () => {
              await resetToday(env);
              setConfirmReset(false);
            }}
          >
            Reset
          </Button>
          <Button onClick={() => setConfirmReset(false)}>Cancel</Button>
        </div>
      </Sheet>
    </div>
  );
}
